import { fetchPaymentsGatewayName } from 'root/api/helpers';

export const getOldestTimeStampFromShopsGet = shops => {
  if (!shops?.length) return '';

  const timestampOrderedShops = shops.sort(
    (a, b) => (a.timeStamp > b.timeStamp) - (a.timeStamp < b.timeStamp)
  );

  return timestampOrderedShops[0].timeStamp;
};

const fetchHasEcom = async ({ accountID }) => {
  const res = await $.get(`/API/Account/${accountID}/Integration.json`);

  let integrations = res.Integration || [];
  if (!Array.isArray(integrations)) {
    integrations = [integrations];
  }

  const integration = integrations.find(
    ({ systemIntegrationID }) => systemIntegrationID === '50' || systemIntegrationID === '55'
  );
  return !!integration;
};

const fetchLocations = async () => {
  const fetchFromAPI = () => {
    return window.lsCloud.getJSONFromAPI('/admin/shop-location/all');
  };

  const fetchFromCache = () => {
    const ttl = 2 * 60 * 1000; // 2min
    const sessionKey = 'fetch_locations';

    const { cached_request: cachedRequest } = window.merchantos;
    const cache = cachedRequest.SessionStorageCache;
    const request = new cachedRequest.CachedAsyncRequest(fetchFromAPI, ttl, cache);

    return request.getResponse(sessionKey);
  };

  try {
    const skipFetchLocationsKey = 'skip_fetch_locations';
    const { localStorage } = window;

    if (localStorage.getItem(skipFetchLocationsKey)) {
      return {};
    }

    let allLocationsUpdated = true;
    const { data = [] } = await fetchFromCache();

    const reducedLocations = data.reduce((prev, curr, index) => {
      const { locationUUID, longitude } = curr;
      const nextIndex = index + 1;

      const flattenedLocation = {
        [`location_${nextIndex}_uuid`]: locationUUID,
        [`location_${nextIndex}_has_address`]: !!longitude,
      };

      if (!longitude) {
        allLocationsUpdated = false;
      }

      return {
        ...prev,
        ...flattenedLocation,
      };
    }, {});

    if (allLocationsUpdated) {
      localStorage.setItem(skipFetchLocationsKey, '1');
    }

    return reducedLocations;
  } catch (e) {
    window.newrelic?.noticeError(e); // eslint-disable-line no-unused-expressions
    return {};
  }
};

class Appcues {
  init() {
    // Appcues initialization script
    (() => {
      if (!window.AppcuesReady) {
        window.AppcuesReady = callback => {
          if (callback) window.AppcuesReady.q.push(callback);
          if (window.Appcues) {
            while (window.AppcuesReady.q.length) {
              const cb = window.AppcuesReady.q.shift();
              if (typeof cb === 'function') cb();
            }
          }
        };
        window.AppcuesReady.q = [];
      }
    })();

    // Bootstrap appcues
    window.AppcuesReady(() => {
      window.merchantos.session.init().done(async () => {
        const session = window.merchantos.session.sessData;

        const { systemCustomerID: accountID, systemCustomerName: accountName, shopCount } = session;
        const { firstName, lastName, email, employeeID } = session.Employee;
        const { shopID, name: shopName } = session.Shop;

        const [hasEcom, gatewayName] = await Promise.all([
          fetchHasEcom({ accountID }),
          fetchPaymentsGatewayName({ accountID, shopID }),
        ]);

        const getModule = moduleName => window.lsCloud.Modules.hasModule(moduleName);

        const locations = window.merchantos.session.hasRight('admin_shops')
          ? await fetchLocations()
          : {};

        // Company information to send to Appcues
        const company = {
          id: accountID,
          name: accountName,
          shop_count: shopCount,
        };

        const shops = await window.lsCloud.Shops.get();
        const oldestTimeStamp = getOldestTimeStampFromShopsGet(shops);

        // User details to send to Appcues
        const appcuesUser = {
          email,
          user_id: `${accountID}-${employeeID}`,
          employee_id: employeeID,
          name: `${firstName} ${lastName}`,
          shop_id: shopID,
          shop_name: shopName,
          owner: window.merchantos.session.hasRight('owner'),
          admin: window.merchantos.session.hasRight('admin'),
          module_advanced_reporting: getModule('advanced_reporting_cloud'),
          module_analytics: getModule('reporting.analytics.autologin'),
          module_customer_loyalty: getModule('customer.loyalty'),
          module_inventory_vendor_catalog_v2: getModule('inventory.vendor_catalog.v2'),
          module_marketing_service: getModule('marketing.service'),
          module_negative_inventory: getModule('inventory.negative_inventory'),
          module_payments_reports_integrated: getModule('payments.reports.integrated'),
          module_payments_reports_settlements: getModule('payments.reports.settlements'),
          module_provisioning_loyalty: getModule('provisioning.loyalty'),
          module_reporting_lightspeed_payments: getModule('reporting.lightspeed_payments'),
          module_settings_register_rules: getModule('settings.register_rules'),
          has_ecom: hasEcom,
          has_integrated_payments: !!gatewayName,
          payments_gateway: gatewayName,
          company,
          accountStatus: window.merchantos.account.status,
          isGriffin: window.isGriffin(),
          shop_creation_date: oldestTimeStamp,
          ...locations,
        };

        // Appcues user
        window.Appcues.identify(accountID, appcuesUser);

        history.onChangeState(
          [
            { name: undefined, form_name: undefined, tab: undefined },
            { pathname: '/register.php', form_name: undefined },
          ],
          () => {
            window.Appcues.page();
          }
        );

        // On the first register load the stateChange is controlled from Client.js
        // and is called before the Appcues stateChange gets registered
        if (window.location.pathname === '/register.php') {
          window.Appcues.page();
        }
      });
    });

    const appID = '46996';
    const [head] = document.getElementsByTagName('head');
    const appcues = document.createElement('script');
    appcues.type = 'text/javascript';
    appcues.async = true;
    appcues.defer = true;
    appcues.src = `//fast.appcues.com/${appID}.js`;
    appcues.onload = () => window.AppcuesReady();
    head.appendChild(appcues);
  }
}

export default new Appcues();
